<template>
  <div class="overflow-auto">
    <item
        v-for="item in sendListConversations"
        :key="item.id"
        :send-list-conversation="item"
        :is-current="isCurrent(item)"
        :is-selected="isSelected(item)"
        :can-make-call="canMakeCall"
        @select="onSelect"
        @edit="onEdit"
        @delete="onDelete"
        @open-chat="onOpenChat"
        @filter="onFilter"
        @toggle="onToggleSelection"
        @restore="onRestore"
        @move="onMove"
        :is-edit="isEdit"
    ></item>
    <infinite-loading @infinite="loadConversations" spinner="waveDots" :distance="600" :identifier="infiniteLoadKey">
      <span slot="no-more"></span>
      <template slot="no-results">
        <h5><span class="badge badge-secondary">No results</span></h5>
      </template>
      <template slot="error" slot-scope="{ trigger }">
        <h5><span class="badge badge-danger">Whoops. It looks like the server is unavailable.</span>
        </h5>
        <div class="text-center">
          <el-button size="mini" @click="trigger()">Retry</el-button>
        </div>
      </template>
    </infinite-loading>
  </div>
</template>

<script>
import Item from './list-item';
import eventBus from '../../../event-bus';
import {mapMutations, mapState} from 'vuex';

export default {
  components: {
    Item,
  },

  props: {
    url: String,
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      sendListConversations: [],
      cancelToken: null,
      isRequestActive: false,
      current: null,
      page: 1,
      infiniteLoadKey: moment().format('x'),
      canMakeCall: false,
    };
  },

  computed: {
    ...mapState('page', {excluded: 'excludedConversationIds'}),
  },

  watch: {
    sendListConversations(values) {
      this.current = _.find(values, {id: _.get(this.current, 'id', null)}, null);
    },

    url(newUrl) {
      this.refresh();
    },

    current(newValue) {
      this.$emit('select', newValue);
    },

    isRequestActive(newValue) {
      if (newValue) {
        this.$emit('refresh-start');
      } else {
        this.$emit('refresh-finish');
      }
    },
  },

  methods: {
    ...mapMutations('page', {
      toggleSelection: 'toggleExcludedConversationId',
      clearSelection: 'clearExcludedConversationIds',
    }),

    /**
     * @param {Object} $state
     * @return {undefined}
     */
    loadConversations($state) {
      this.performLoading($state);
    },

    /**
     * @param {Object|null} $state
     * @return {undefined}
     */
    performLoading($state = null) {
      if (this.isRequestActive) {
        this.cancelToken.cancel();
      } else {
        this.isRequestActive = true;
      }

      this.cancelToken = axios.CancelToken.source();

      axios.get(this.url, {
        cancelToken: this.cancelToken.token,
        params: {page: this.page},
      })
          .then(data => this.onLoadSuccess(data, $state))
          .catch(error => this.onLoadError(error, $state))
          .then((isCancelled) => {
            if (!isCancelled) {
              this.isRequestActive = false;
            }
          });
    },

    /**
     * @param {Object} data
     * @param {Object} $state
     * @return {Boolean}
     */
    onLoadSuccess({data: {data: items, meta: {current_page, last_page, can_make_call}}}, $state) {
      this.canMakeCall = can_make_call;

      if (_.size(items)) {
        this.sendListConversations.push(...items);
        $state && $state.loaded();
      }

      if (current_page < last_page) {
        this.page++;
      } else {
        $state && $state.complete();
      }

      return false;
    },

    /**
     * @param {Object} error
     * @param {Object} $state
     * @return {Boolean}
     */
    onLoadError(error, $state) {
      if (axios.isCancel(error)) {
        return true;
      }
      $state && $state.error();

      return false;
    },

    /**
     * @param {Object} item
     */
    onSelect(item) {
      this.current = item;
    },

    /**
     * @param {Object} item
     */
    onEdit(item) {
      this.$emit('edit', item);
    },

    /**
     * @param {Object} item
     */
    onDelete(item) {
      this.$emit('delete', item);
    },

    /**
     * @param {object} payload
     */
    onOpenChat(payload) {
      this.$emit('open-chat', payload);
    },

    /**
     * @param {Object} item
     * @emits restore
     */
    onRestore(item) {
      this.$emit('restore', item);
    },

    /**
     * @param {String} url
     * @emits move
     */
    onMove(url) {
      this.$emit('move', url);
    },

    /**
     * @return {undefined}
     */
    refresh() {
      this.sendListConversations = [];
      this.page = 1;
      this.infiniteLoadKey = moment().format('x');
    },

    /**
     * @param {Object} item
     * @return {Boolean}
     */
    isCurrent(item) {
      return item.id === _.get(this.current, 'id');
    },

    /**
     * @param {Number} id
     */
    removeItemById(id) {
      const index = _.findIndex(this.sendListConversations, {id: id});
      if (index < 0) {
        return;
      }
      this.sendListConversations.splice(index, 1);
    },

    /**
     * @param {Object} item
     */
    editItem(item) {
      const index = _.findIndex(this.sendListConversations, function(val) {
        return _.get(val, 'conversation.id') === item.id;
      });

      this.$set(this.sendListConversations[index], 'conversation', item);
    },

    /**
     * @param {Number} brandId
     * @param {Number} contactId
     * @param {Object|null} model
     */
    onContactNoteStored({brandId, contactId, model}) {
      _.each(this.sendListConversations || [], (sendListConversation, index) => {
        if (_.get(sendListConversation, 'conversation.contact_id') !== contactId) {
          return;
        }

        if (_.get(sendListConversation, 'conversation.outgoing_number.brand_id') !== brandId) {
          return;
        }

       this.$set(this.sendListConversations[index].conversation, 'note', model);
      });
    },

    /**
     * @param {Number} conversationId
     * @param {Object|null} list
     */
    onScheduleListChanged({conversationId, list = []}) {
      _.each(this.sendListConversations || [], (sendListConversation, index) => {
        if (_.get(sendListConversation, 'conversation.id') !== conversationId) {
          return;
        }

        this.$set(this.sendListConversations[index].conversation, 'pending_messages', list);
      });
    },

    onFilter(contact) {
      this.$store.commit('sendListFilter/updateItems', {
        'contact_id': {
          options: [contact],
        }
      });

      this.$nextTick(function () {
        this.$store.commit('sendListFilter/updateFilters', {
          name: 'contact_id',
          values: {contact_id: String(contact.id)}
        });
      });
    },

    /**
     * @param {Object} sendListConversation
     */
    onToggleSelection({conversation_id: conversationId}) {
      this.toggleSelection({conversationId});
    },

    /**
     * @param conversation_id
     * @return {boolean}
     */
    isSelected({conversation_id: conversationId}) {
      return !_.includes(this.excluded, conversationId);
    },
  },

  created() {
    eventBus.$on('contact-note-stored', this.onContactNoteStored);
    eventBus.$on('schedule-list-changed', this.onScheduleListChanged);
  },

  beforeDestroy() {
    eventBus.$off('contact-note-stored', this.onContactNoteStored);
    eventBus.$off('schedule-list-changed', this.onScheduleListChanged);
    this.clearSelection();
  }
}
</script>
